import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchSubscriptionPlans } from "../api/subscriptionPlans";

const COLORS = {
  primary: "#3AAFA9",
  textMain: "#2E2E2E",
  textSub: "#4B5563",
  textMuted: "#9CA3AF",
  line: "#E6E8EC",
  error: "#EF4444",
};



const BILLING_CONFIG = {
  monthly: {
    apiKey: "1_month",
    priceKey: "price_monthly",
    label: "Monthly",
    suffix: "/month",
    savings: null,
  },
  quarterly: {
    apiKey: "3_month",
    priceKey: "price_3_month",
    label: "Quarterly",
    suffix: "/3 months",
    savings: "3 Month Plan",
  },
  halfYearly: {
    apiKey: "6_month",
    priceKey: "price_6_month",
    label: "Half Yearly",
    suffix: "/6 months",
    savings: "6 Month Plan",
  },
  yearly: {
    apiKey: "12_month",
    priceKey: "price_yearly",
    label: "Yearly",
    suffix: "/year",
    savings: "Yearly Plan",
  },
};

const BILLING_OPTIONS = ["monthly", "quarterly", "halfYearly", "yearly"];

const parseFeatures = (value) => {
  try {
    const parsed = JSON.parse(String(value || "[]"));
    return Array.isArray(parsed)
      ? parsed.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
};

const stripHtml = (value = "") =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolvePlanType = (plan = {}) => {
  const source = [plan.plan_key, plan.plan_heading, plan.title, plan.plan_type]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");

  if (source.includes("complete")) return "complete";
  if (source.includes("premium")) return "premium";
  if (source.includes("essential")) return "essential";
  if (source.includes("custom")) return "custom";
  return String(plan.title || plan.plan_type || "plan").trim().toLowerCase();
};

const normalizeBackendPlans = (plans, billing) => {
  const config = BILLING_CONFIG[billing] || BILLING_CONFIG.monthly;
  const byType = new Map();

  plans.forEach((plan) => {
    const type = resolvePlanType(plan);
    if (!type || byType.has(type)) return;

    const price = Number(plan?.[config.priceKey] || 0);
    if (!price) return;

    byType.set(type, {
      id: plan.id || plan.plan_unique_id || plan.plan_key || type,
      type,
      name: plan.title || plan.plan_type || "Care Plan",
      desc: stripHtml(plan.subtitle) || "Care subscription plan",
      price,
      suffix: config.suffix,
      savings: config.savings,
      badge: plan.plan_heading || (plan.is_most_popular ? "Most Popular" : "Care Plan"),
      highlight: Boolean(plan.is_most_popular) || type === "complete",
      features: parseFeatures(plan.features_json),
      ctaText: plan.cta_text || `Start ${plan.title || "Plan"}`,
    });
  });

  return Array.from(byType.values()).sort((a, b) => {
    const order = { essential: 1, complete: 2, premium: 3, custom: 4 };
    return (order[a.type] || 10) - (order[b.type] || 10);
  });
};

const Subscription = () => {
  const [billing, setBilling] = useState("monthly");
  const [backendPlans, setBackendPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const navigate = useNavigate();

  const pricing = useMemo(
    () => {
      return normalizeBackendPlans(backendPlans, billing);
    },
    [backendPlans, billing],
  );

  useEffect(() => {
    let isMounted = true;
    const config = BILLING_CONFIG[billing] || BILLING_CONFIG.monthly;

    setPlansLoading(true);
    fetchSubscriptionPlans(config.apiKey)
      .then((plans) => {
        if (isMounted) setBackendPlans(plans);
      })
      .catch((error) => {
        console.warn("Subscription plans API unavailable.", error);
        if (isMounted) setBackendPlans([]);
      })
      .finally(() => {
        if (isMounted) setPlansLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [billing]);

  return (
    <section
      className="relative overflow-x-hidden  px-6 pt-12 pb-24 sm:px-10 sm:pt-16"
      style={{
        background:
          "radial-gradient(circle at 12% 20%, rgba(58,175,169,0.14), transparent 32%), radial-gradient(circle at 86% 78%, rgba(58,175,169,0.1), transparent 36%), #F7FAF9",
      }}
    >
      <div className="absolute -left-24 top-12 h-72 w-72 rounded-full bg-[#3AAFA9]/10 blur-3xl" />
      <div className="absolute -right-28 bottom-10 h-80 w-80 rounded-full bg-[#3AAFA9]/15 blur-3xl" />

      <div className="relative mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div
            className="mx-auto inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em]"
            style={{
              borderColor: `${COLORS.primary}66`,
              backgroundColor: `${COLORS.primary}1A`,
              color: COLORS.primary,
            }}
          >
            <Sparkles size={14} />
            Simple & Transparent
          </div>
          <h1
            className="mt-5 text-4xl font-extrabold sm:text-5xl"
            style={{ color: COLORS.textMain }}
          >
            Pricing That Makes Sense
          </h1>
          <p
            className="mx-auto mt-4 max-w-2xl text-sm sm:text-base"
            style={{ color: COLORS.textSub }}
          >
            No hidden fees. No surprises. Just honest pricing for quality care.
          </p>
        </motion.div>

        <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center justify-center gap-3 rounded-[28px] border bg-white/75 p-3 shadow-[0_18px_50px_rgba(16,42,42,0.08)] backdrop-blur sm:flex-row sm:gap-4">
          <div
            className="grid w-full grid-cols-2 gap-1 rounded-2xl border bg-white p-1 text-sm font-semibold shadow-sm sm:w-auto sm:grid-cols-4 sm:rounded-full"
            style={{ borderColor: COLORS.line }}
          >
            {BILLING_OPTIONS.map((option) => {
              const isActive = billing === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setBilling(option)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition ${
                    isActive ? "text-white shadow-sm" : "text-gray-500"
                  }`}
                  style={{
                    background: isActive ? COLORS.primary : "transparent",
                  }}
                >
                  {BILLING_CONFIG[option].label}
                </button>
              );
            })}
          </div>
          {billing !== "monthly" && (
            <span
              className="rounded-full border px-3 py-1 text-xs font-semibold"
              style={{
                borderColor: `${COLORS.primary}66`,
                color: COLORS.primary,
                backgroundColor: `${COLORS.primary}12`,
              }}
            >
              {(BILLING_CONFIG[billing] || BILLING_CONFIG.monthly).savings}
            </span>
          )}
          {plansLoading && (
            <span
              className="rounded-full border px-3 py-1 text-xs font-semibold"
              style={{
                borderColor: COLORS.line,
                color: COLORS.textSub,
                backgroundColor: "#FFFFFF",
              }}
            >
              Loading latest plans...
            </span>
          )}
        </div>

        {plansLoading ? (
          <div className="text-center py-20">
            <span className="text-gray-500 font-semibold text-sm">Loading subscription plans...</span>
          </div>
        ) : pricing.length === 0 ? (
          <div className="text-center py-20">
            <span className="text-gray-500 font-semibold text-sm">No subscription plans currently available.</span>
          </div>
        ) : (
          <div
            className={`mx-auto mt-12 grid max-w-5xl gap-6 ${
              pricing.length <= 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"
            }`}
          >
            {pricing.map((plan, index) => (
              <motion.div
                key={plan.id || plan.name}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`relative flex h-full min-h-[560px] flex-col overflow-hidden rounded-3xl border bg-white p-7 sm:p-8 shadow-[0_24px_60px_rgba(16,42,42,0.08)] ${
                  plan.highlight
                    ? "ring-1 ring-[#3AAFA9]/70 shadow-[0_30px_70px_rgba(58,175,169,0.22)]"
                    : ""
                }`}
                style={{ borderColor: `${COLORS.primary}20` }}
              >
                <h3
                  className="text-2xl font-bold"
                  style={{ color: COLORS.textMain }}
                >
                  {plan.name}
                </h3>
                <p className="mt-2 text-sm" style={{ color: COLORS.textSub }}>
                  {plan.desc}
                </p>
                <div className="mt-6 flex items-end gap-2">
                  <span
                    className="text-4xl font-extrabold"
                    style={{ color: COLORS.textMain }}
                  >
  ₹{plan.price.toLocaleString("en-IN")}
                  </span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: COLORS.textMuted }}
                  >
                    {plan.suffix}
                  </span>
                  {plan.savings && (
                    <span className="ml-auto rounded-full bg-[#3AAFA9]/10 px-3 py-1 text-xs font-semibold text-[#3AAFA9]">
                      {plan.savings}
                    </span>
                  )}
                </div>

                <div className="mt-6 flex-1 space-y-3">
                  {(plan.features || []).map((feature) => (
                    <div
                      key={feature}
                      className="flex items-start gap-2 text-sm"
                      style={{ color: COLORS.textSub }}
                    >
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: COLORS.primary }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/contact")}
                  className="mt-8 w-full rounded-full px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5"
                  style={{
                    background: plan.highlight
                      ? "linear-gradient(135deg, #3AAFA9, #2F9F98)"
                      : COLORS.primary,
                    boxShadow: "0 16px 30px rgba(58,175,169,0.25)",
                  }}
                >
                  {plan.ctaText || `Start ${plan.name}`}
                </button>

              </motion.div>
            ))}
          </div>
        )}

        <div
          className="mx-auto mt-10 grid max-w-5xl gap-4 rounded-3xl border bg-white/80 p-6 text-sm shadow-sm sm:grid-cols-3"
          style={{ borderColor: COLORS.line }}
        >
          {[
            "Dedicated care guidance for every plan",
            "Care-ready onboarding in 24 hours",
            "All plans include secure data handling",
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-2"
              style={{ color: COLORS.textSub }}
            >
              <CheckCircle2 size={16} style={{ color: COLORS.primary }} />
              {item}
            </div>
          ))}
        </div>
      </div>
      {/* PRICING QUESTIONS */}
<div className="mt-20 max-w-4xl mx-auto">
  <h2
    className="text-3xl sm:text-4xl font-bold text-center mb-10"
    style={{ color: COLORS.textMain }}
  >
    Pricing Questions
  </h2>

  <div className="space-y-6">
	    {[
	      {
	        q: "Is the payment refundable?",
	        a: "Yes, eligible payments are refundable as per Janak's refund policy. Refund approval depends on service status, plan usage, and verification by our support team.",
	      },
	      {
	        q: "Are there any hidden charges?",
	        a: "No hidden platform charges are added without informing you. Any extra real-world expense, such as medicine, diagnostics, hospital bills, or third-party charges, is shared for approval before payment.",
	      },
	      {
	        q: "When does the plan start?",
	        a: "Your plan starts after payment confirmation and onboarding verification. Our team confirms the care details before service activation.",
	      },
	      {
		q: "What payment methods do you accept?",
	        a: "We accept supported digital payment methods such as UPI, cards, and net banking. Available methods may vary based on your location and payment gateway availability.",
	      },
	      {
	        q: "Can the plan be changed later?",
	        a: "Yes, you can request a plan change through the Janak team. Any price difference, refund, or adjustment is reviewed based on the selected plan and current service status.",
	      },
	    ].map((item, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        viewport={{ once: true }}
        className="bg-white rounded-xl p-6 shadow-sm border"
        style={{ borderColor: COLORS.line }}
      >
        <h3
          className="text-lg font-semibold mb-2"
          style={{ color: COLORS.textMain }}
        >
          {item.q}
        </h3>
        <p
          className="text-sm leading-relaxed"
          style={{ color: COLORS.textSub }}
        >
          {item.a}
        </p>
      </motion.div>
    ))}
  </div>
</div>

    </section>
  );
};

export default Subscription;
